import * as React from 'react';
import { makeStyles } from '@material-ui/core';
import { fontFamilies } from '../../theme/text';
import { SankeyData } from './sankey-interfaces';
import useSankey from './useSankey';
import { select } from 'd3';
import { sankeyLinkHorizontal } from 'd3-sankey';

export interface SankeyProps {
  /** Data: lists of Nodes and Corresponding Links plus data units */
  data: SankeyData;
  /** Display options for the chart */
  options?: Partial<SankeyOptions>;
}

/** Display options for the chart. All optional */
export interface SankeyOptions {
  /** Width of the svg viewbox */
  svgWidth: number;
  /** Height of the svg viewbox */
  svgHeight: number;
  /** Size of text labels, adjust to suit the size of the viewbox */
  fontSize: number;
}

const useStyles = makeStyles({
  root: {
    width: '100%',
    height: '100%'
  },
  svg: {
    fontFamily: fontFamilies.body,
    height: '100%',
    width: '100%'
  }
});

/**
 * ## Sankey Chart
 * Render a Sankey diagram illustrating flow between multiple points in a system
 */
const Sankey = ({ data, options }: SankeyProps) => {
  const classes = useStyles();
  const nodeRef = React.useRef<SVGGElement>();
  const linkRef = React.useRef<SVGGElement>();
  const textRef = React.useRef<SVGGElement>();

  const localOptions: SankeyOptions = React.useMemo(() => {
    const defaults: SankeyOptions = {
      svgWidth: 800,
      svgHeight: 100,
      fontSize: 20
    };
    return options != undefined
      ? {
          svgWidth: options.svgWidth != undefined ? options.svgWidth : defaults.svgWidth,
          svgHeight: options.svgHeight != undefined ? options.svgHeight : defaults.svgHeight,
          fontSize: options.fontSize != undefined ? options.fontSize : defaults.fontSize
        }
      : defaults;
  }, []);

  const { layoutGenerator, format } = useSankey(localOptions);

  const { nodes, links } = React.useMemo(() => {
    return layoutGenerator(data.nodes, data.links);
  }, []);

  React.useEffect(() => {
    select(nodeRef.current)
      .attr('stroke', '#000')
      .selectAll('rect')
      .data(nodes)
      .join('rect')
      .attr('x', (d) => d.x0)
      .attr('y', (d) => d.y0)
      .attr('height', (d) => d.y1 - d.y0)
      .attr('width', (d) => d.x1 - d.x0)
      .attr('fill', (d) => d.color)
      .append('title')
      .text((d) => `${d.name}\n${format(d.value, data.units)}`);

    select(textRef.current)
      .attr('font-family', 'sans-serif')
      .attr('font-size', localOptions.fontSize)
      .selectAll('text')
      .data(nodes)
      .join('text')
      .attr('x', (d) => (d.x0 < localOptions.svgWidth / 2 ? d.x1 + 6 : d.x0 - 6))
      .attr('y', (d) => (d.y1 + d.y0) / 2)
      .attr('dy', '0.35em')
      .attr('text-anchor', (d) => (d.x0 < localOptions.svgWidth / 2 ? 'start' : 'end'))
      .attr('fill', '#000')
      .attr('stroke', 'none')
      .text((d) => d.name);
  }, [nodes]);

  React.useEffect(() => {
    const link = select(linkRef.current)
      .attr('fill', 'none')
      .attr('stroke-opacity', 0.5)
      .selectAll('g')
      .data(links)
      .join('g')
      .style('mix-blend-mode', 'multiply');

    link
      .append('path')
      .attr('d', sankeyLinkHorizontal())
      // @ts-ignore: Property 'x' does not exist on type 'Y'
      .attr('stroke', (d) => d.target.color)
      .attr('stroke-width', (d) => Math.max(1, d.width));

    link
      .append('title')
      // @ts-ignore: Property 'x' does not exist on type 'Y'
      .text((d) => `${d.source.name} → ${d.target.name}\n${format(d.value, data.units)}`);
  }, [links]);

  return (
    <div className={classes.root}>
      <svg
        className={classes.svg}
        viewBox={`0 0 ${localOptions.svgWidth} ${localOptions.svgHeight}`}
      >
        <g ref={nodeRef}></g>
        <g ref={linkRef}></g>
        <g ref={textRef}></g>
      </svg>
    </div>
  );
};

export default Sankey;
